/**
 * Click-to-open per-cell popover. Renders the cell's metadata
 * (state pill + linked IBSes + MVB + open findings + dependencies)
 * + a "trace state" button that opens the constellation's
 * existing ProvenanceTrace overlay -- reuses the P83 component
 * with chain="resilience-state", entity_kind="capability".
 *
 * Per the handoff: positions next to the clicked cell, clamped
 * to the viewport. The page shell passes the absolute anchor
 * coords.
 */
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";

import { useMe } from "shared/api/useMe";
import { ProvenanceTrace, type TraceCoords } from "twin/constellation/ProvenanceTrace";
import { useState } from "react";
import type { CapabilityCell } from "shared/api/types";
import type { CapabilityState } from "twin/capmap/stateMap";

export interface CapabilityPopoverProps {
  cap: CapabilityCell;
  state: CapabilityState;
  /** Absolute coords inside the stage container */
  anchor: { x: number; y: number };
  isOrigin: boolean;
  /** Round 13 D4: the upstream capabilities BY NAME, not a count. */
  dependencyNames: string[];
  onClose: () => void;
}

const STATE_LABEL: Record<CapabilityState, string> = {
  down: "Down",
  degraded: "Degraded",
  "at-risk": "At risk",
  stable: "Stable",
};

const STATE_DOT: Record<CapabilityState, string> = {
  down: "#E2574A",
  degraded: "#D79A3A",
  "at-risk": "#D79A3A",
  stable: "#5AAE81",
};

export function CapabilityPopover({
  cap,
  state,
  anchor,
  isOrigin,
  dependencyNames,
  onClose,
}: CapabilityPopoverProps): JSX.Element {
  const { t } = useTranslation();
  const me = useMe();
  const [traceCoords, setTraceCoords] = useState<TraceCoords | null>(null);

  const openTrace = () =>
    setTraceCoords({
      chain: "resilience-state",
      entity_kind: "capability",
      entity_id: cap.id,
    });

  return (
    <>
      <div
        data-testid="capmap-popover"
        className="capmap-popover"
        style={{ left: anchor.x, top: anchor.y }}
        onClick={(e) => e.stopPropagation()}
      >
        <button
          type="button"
          data-testid="capmap-popover-close"
          className="capmap-popover-close"
          onClick={onClose}
          aria-label={t("modules.twin.capmap.popover.close")}
        >
          ×
        </button>
        <div className="capmap-popover-kicker">
          {cap.zone_code ?? t("modules.twin.capmap.popover.unzoned")}
          {cap.mvb_flag ? t("screens.capabilityPopover.mvb") : ""}
        </div>
        <h4 data-testid="capmap-popover-name">{cap.name}</h4>

        <button
          type="button"
          data-testid="capmap-popover-state-pill"
          className={`capmap-popover-statpill capmap-state-${state}`}
          onClick={openTrace}
          title={t("modules.twin.capmap.popover.stateTrace")}
        >
          <span
            className="capmap-popover-statdot"
            style={{ background: STATE_DOT[state] }}
          />
          {STATE_LABEL[state]}
          {isOrigin ? ` · ${t("modules.twin.capmap.popover.incidentSource")}` : ""}
        </button>

        <dl className="capmap-popover-kv">
          <div className="capmap-popover-row">
            <dt>{t("modules.twin.capmap.popover.linkedIbs")}</dt>
            <dd>{cap.linked_ibs_ids.length}</dd>
          </div>
          <div className="capmap-popover-row">
            <dt>{t("modules.twin.capmap.popover.inMvb")}</dt>
            <dd>
              {cap.mvb_flag
                ? t("screens.capabilityPopover.yes")
                : t("screens.capabilityPopover.no")}
            </dd>
          </div>
          <div className="capmap-popover-row">
            <dt>{t("modules.twin.capmap.popover.openFindings")}</dt>
            <dd>{cap.open_finding_count}</dd>
          </div>
          <div className="capmap-popover-row">
            <dt>{t("modules.twin.capmap.popover.dependencies")}</dt>
            {/* D4 -- named, not counted. A count answers "how many"
                and leaves "which" nowhere on the product. Capped,
                with the remainder stated, because a popover is not a
                register and a silent truncation would be the same
                defect in a smaller box. */}
            <dd data-testid="capmap-popover-dependencies">
              {dependencyNames.length === 0
                ? t("modules.twin.capmap.popover.noDependencies")
                : dependencyNames.slice(0, 5).join(" · ") +
                  (dependencyNames.length > 5
                    ? " · " +
                      t("modules.twin.capmap.popover.andMoreDependencies", {
                        count: dependencyNames.length - 5,
                      })
                    : "")}
            </dd>
          </div>
          <div className="capmap-popover-row">
            <dt>{t("modules.twin.capmap.popover.tier")}</dt>
            <dd>{cap.tier}</dd>
          </div>
        </dl>

        {/* Round 10 A6 -- the cells lead somewhere.
            Every cell on this map was a button that opened a
            popover and stopped: a reader could see that a
            capability was impaired and had no way to reach the
            capability. */}
        <Link
          to={`/twin/capability/${cap.id}`}
          data-testid="capmap-popover-open"
          className="capmap-popover-open"
        >
          {t("modules.twin.capmap.popover.openCapability")}
        </Link>

        <button
          type="button"
          data-testid="capmap-popover-derive"
          className="capmap-popover-derive"
          onClick={openTrace}
        >
          {t("modules.twin.capmap.popover.deriveButton")}
        </button>
      </div>

      <ProvenanceTrace
        coords={traceCoords}
        onClose={() => setTraceCoords(null)}
        onDrillInto={(next) => setTraceCoords(next)}
        isAdmin={me.data?.is_admin ?? false}
      />
    </>
  );
}
